/**
 * VictoryScene - Displayed after completing Level 10
 *
 * Features:
 * - "You Beat the Game!" celebration header
 * - Total stats summary (lifetime totals from SaveSystem)
 * - "Ready for a harder challenge?" prompt
 * - "Start Tier X" and "Return to Menu" buttons
 * - Celebration particles
 *
 * Per PRD section 5.2 and Task #24 specifications
 */

import Phaser from 'phaser';
import {
  COLORS,
  COLOR_CSS,
  GAME_WIDTH,
  GAME_HEIGHT,
  FONTS,
  TEXT_SIZES,
  SPACING,
  DURATIONS,
  SAFE_AREA,
} from '../config';
import { Button } from '../ui/Button';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';
import { logSceneEvent } from '../utils/automation';
import { createCommandDeckBackground, createSceneHeader } from '../ui/SceneChrome';

/**
 * Data passed to VictoryScene when starting it
 */
export interface VictorySceneData {
  /** The tier that was just completed */
  tier: number;
  /** Score breakdown from the final level */
  breakdown: {
    kills: number;
    gateBonus: number;
    armyBonus: number;
    total: number;
  };
}

export class VictoryScene extends Phaser.Scene {
  private sceneData!: VictorySceneData;
  private particles: Phaser.GameObjects.Graphics[] = [];

  constructor() {
    super({ key: 'VictoryScene' });
  }

  init(data: VictorySceneData): void {
    this.sceneData = data;

    // Save high score for the final level and update stats was already done in ResultsScene
    // VictoryScene is called after ResultsScene saves the score
  }

  create(): void {
    logSceneEvent('VictoryScene');
    // Fade in
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);

    // Play victory music sting
    audioSystem.playMusic('victory');

    this.createBackground();
    this.createParticles();
    this.createHeader();
    this.createStatsCard();
    this.createChallengePrompt();
    this.createButtons();
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the background
   */
  private createBackground(): void {
    createCommandDeckBackground(this, COLORS.gold);
  }

  /**
   * Creates celebration particles (confetti/stars)
   */
  private createParticles(): void {
    // Create multiple particle emitters for a confetti effect
    const particleCount = 50;
    const colors = [COLORS.gold, COLORS.player, COLORS.gate, 0xFFFFFF];

    for (let i = 0; i < particleCount; i++) {
      const particle = this.add.graphics();
      const color = colors[i % colors.length];
      const size = Phaser.Math.Between(4, 10);
      const shape = i % 3; // 0 = circle, 1 = square, 2 = star

      particle.fillStyle(color, 1);

      if (shape === 0) {
        particle.fillCircle(0, 0, size / 2);
      } else if (shape === 1) {
        particle.fillRect(-size / 2, -size / 2, size, size);
      } else {
        // Simple star shape (diamond)
        particle.fillTriangle(0, -size, -size / 2, 0, size / 2, 0);
        particle.fillTriangle(0, size, -size / 2, 0, size / 2, 0);
      }

      particle.x = Phaser.Math.Between(0, GAME_WIDTH);
      particle.y = -20 - Phaser.Math.Between(0, GAME_HEIGHT);

      this.particles.push(particle);

      // Animate particle falling
      const duration = Phaser.Math.Between(3000, 6000);
      const delay = Phaser.Math.Between(0, 2000);

      this.tweens.add({
        targets: particle,
        y: GAME_HEIGHT + 50,
        x: particle.x + Phaser.Math.Between(-50, 50),
        rotation: Phaser.Math.Between(0, 6),
        duration,
        delay,
        ease: 'Sine.easeIn',
        repeat: -1,
        onRepeat: () => {
          particle.x = Phaser.Math.Between(0, GAME_WIDTH);
          particle.y = -20;
        },
      });
    }
  }

  /**
   * Creates the victory header
   */
  private createHeader(): void {
    const headerY = SAFE_AREA.top + SPACING.space6;

    // Trophy/star emoji representation using text
    const trophy = this.add.text(
      GAME_WIDTH / 2,
      headerY - 10,
      '★',
      {
        fontFamily: FONTS.display,
        fontSize: '56px',
        color: COLOR_CSS.gold,
      }
    ).setOrigin(0.5);

    // Pulsing glow on trophy
    this.tweens.add({
      targets: trophy,
      scale: 1.2,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const header = createSceneHeader(this, {
      title: 'Core Secured',
      kicker: 'Campaign complete // swarm contained',
      accent: COLORS.gold,
      y: headerY + 56,
    });

    // Subtle scale pulse on header
    this.tweens.add({
      targets: header,
      scale: 1.05,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Tier indicator
    const tierText = this.sceneData.tier > 1 ? `Tier ${this.sceneData.tier}` : 'Tier 1';
    this.add.text(
      GAME_WIDTH / 2,
      headerY + 90,
      `${tierText} Complete!`,
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.body}px`,
        color: COLOR_CSS.gate,
      }
    ).setOrigin(0.5);
  }

  /**
   * Creates the stats summary card
   */
  private createStatsCard(): void {
    const cardY = GAME_HEIGHT * 0.32;
    const cardWidth = GAME_WIDTH - SPACING.space6 * 2;
    const cardHeight = 180;

    // Card background
    const card = this.add.graphics();
    card.fillStyle(COLORS.bgOverlay, 0.7);
    card.fillRoundedRect(
      (GAME_WIDTH - cardWidth) / 2,
      cardY,
      cardWidth,
      cardHeight,
      16
    );

    // Card title
    this.add.text(
      GAME_WIDTH / 2,
      cardY + 20,
      'YOUR JOURNEY',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.body}px`,
        color: COLOR_CSS.uiTextSecondary,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5, 0);

    // Get stats from save system
    const stats = saveSystem.getStats();

    // Stats rows
    const rowStartY = cardY + 55;
    const rowHeight = 32;
    const labelX = GAME_WIDTH / 2 - 70;
    const valueX = GAME_WIDTH / 2 + 90;

    const rowStyle = {
      fontFamily: FONTS.body,
      fontSize: `${TEXT_SIZES.caption}px`,
      color: COLOR_CSS.uiText,
    };

    const valueStyle = {
      fontFamily: FONTS.numbers,
      fontSize: `${TEXT_SIZES.body}px`,
      color: COLOR_CSS.gold,
    };

    // Levels Completed
    this.add.text(labelX, rowStartY, 'Levels Completed', rowStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY, String(stats.totalLevelsCompleted), valueStyle).setOrigin(1, 0.5);

    // Enemies Defeated
    this.add.text(labelX, rowStartY + rowHeight, 'Enemies Defeated', rowStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight, this.formatNumber(stats.totalKills), valueStyle).setOrigin(1, 0.5);

    // Soldiers Collected
    this.add.text(labelX, rowStartY + rowHeight * 2, 'Soldiers Collected', rowStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight * 2, this.formatNumber(stats.totalSoldiersCollected), valueStyle).setOrigin(1, 0.5);

    // Play Time
    this.add.text(labelX, rowStartY + rowHeight * 3, 'Time Played', rowStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight * 3, this.formatTime(stats.totalPlayTimeSeconds), valueStyle).setOrigin(1, 0.5);
  }

  /**
   * Creates the "Ready for a harder challenge?" prompt
   */
  private createChallengePrompt(): void {
    const promptY = GAME_HEIGHT * 0.6;

    this.add.text(
      GAME_WIDTH / 2,
      promptY,
      'Ready for a harder challenge?',
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.uiText,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);

    // Tier preview
    const nextTier = this.sceneData.tier + 1;
    this.add.text(
      GAME_WIDTH / 2,
      promptY + 30,
      `Tier ${nextTier}: Faster enemies, tougher battles!`,
      {
        fontFamily: FONTS.body,
        fontSize: `${TEXT_SIZES.caption}px`,
        color: COLOR_CSS.uiTextSecondary,
      }
    ).setOrigin(0.5);
  }

  /**
   * Creates the navigation buttons
   */
  private createButtons(): void {
    const buttonWidth = 220;
    const buttonHeight = 56;
    const buttonSpacing = SPACING.space3;
    const startY = GAME_HEIGHT * 0.74;

    // Start Next Tier button
    new Button(this, {
      x: GAME_WIDTH / 2,
      y: startY,
      text: `Start Tier ${this.sceneData.tier + 1}`,
      variant: 'primary',
      width: buttonWidth,
      height: buttonHeight,
      onClick: () => this.startNextTier(),
    });

    // Return to Menu button
    new Button(this, {
      x: GAME_WIDTH / 2,
      y: startY + buttonHeight + buttonSpacing,
      text: 'Return to Menu',
      variant: 'secondary',
      width: buttonWidth,
      height: 48,
      onClick: () => this.goToMenu(),
    });
  }

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================

  /**
   * Starts the next tier
   */
  private startNextTier(): void {
    audioSystem.play('button_tap');

    // Advance tier in save system
    saveSystem.advanceTier();

    this.transitionTo('GameScene', {
      level: 1,
      tier: this.sceneData.tier + 1,
    });
  }

  /**
   * Returns to the main menu
   */
  private goToMenu(): void {
    audioSystem.play('button_tap');
    this.transitionTo('TitleScene');
  }

  /**
   * Handles scene transition with fade
   */
  private transitionTo(sceneKey: string, data?: object): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(sceneKey, data);
    });
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Formats a number with commas (e.g., 1234 -> "1,234")
   */
  private formatNumber(num: number): string {
    return num.toLocaleString();
  }

  /**
   * Formats seconds into a readable time string (e.g., "2h 34m")
   */
  private formatTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      const secs = seconds % 60;
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  /**
   * Placeholder for sound effects (implemented in Task #28)
   */
}
